import React, { useEffect, useState } from 'react';
import { cierreCaja, getCierres, getPagos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Calculator, CheckCircle, AlertCircle, TrendingUp, Receipt } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

export default function CajaPage() {
    const { user, hasRole } = useAuth();
    const [pagosHoy, setPagosHoy] = useState([]);
    const [cierres, setCierres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({
        efectivo_entregado: '',
        notas: '',
    });

    const hoy = new Date().toISOString().split('T')[0];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [pagosRes, cierresRes] = await Promise.all([
                getPagos({ fecha_desde: hoy }),
                hasRole(['admin', 'supervisor']) ? getCierres() : Promise.resolve({ data: [] })
            ]);
            setPagosHoy(pagosRes.data.filter(p => p.fecha.startsWith(hoy)));
            setCierres(cierresRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const totalSistema = pagosHoy.reduce((sum, p) => sum + p.monto, 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                efectivo_entregado: parseFloat(form.efectivo_entregado),
                notas: form.notas || null,
            };
            const { data: result } = await cierreCaja(data);
            
            if (result.estado === 'cuadrado') {
                toast.success('Caja cerrada correctamente');
            } else if (result.estado === 'faltante') {
                toast.warning(`Caja con faltante de ${formatCurrency(Math.abs(result.diferencia))}`);
            } else {
                toast.info(`Caja con sobrante de ${formatCurrency(result.diferencia)}`);
            }
            
            setDialogOpen(false);
            setForm({ efectivo_entregado: '', notas: '' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al cerrar caja');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-64" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="caja-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Caja</h1>
                    <p className="text-muted-foreground mt-1">Control de caja del día {hoy}</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="cierre-caja-btn">
                            <Calculator className="w-4 h-4 mr-2" />
                            Cierre de Caja
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-heading">Cierre de Caja</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <Card className="bg-muted/50">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Total en Sistema</span>
                                        <span className="text-xl font-mono font-bold text-primary">
                                            {formatCurrency(totalSistema)}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {pagosHoy.length} pagos registrados hoy
                                    </p>
                                </CardContent>
                            </Card>

                            <div className="space-y-2">
                                <Label htmlFor="efectivo">Efectivo Entregado</Label>
                                <Input
                                    id="efectivo"
                                    type="number"
                                    value={form.efectivo_entregado}
                                    onChange={(e) => setForm({ ...form, efectivo_entregado: e.target.value })}
                                    required
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    data-testid="efectivo-entregado-input"
                                />
                            </div>

                            {form.efectivo_entregado && (
                                <Card className={`${
                                    parseFloat(form.efectivo_entregado) === totalSistema 
                                        ? 'bg-accent/10 border-accent' 
                                        : parseFloat(form.efectivo_entregado) < totalSistema
                                            ? 'bg-destructive/10 border-destructive'
                                            : 'bg-warning/10 border-warning'
                                }`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium">Diferencia</span>
                                            <span className="font-mono font-bold">
                                                {formatCurrency(parseFloat(form.efectivo_entregado) - totalSistema)}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="notas">Notas (opcional)</Label>
                                <Input
                                    id="notas"
                                    value={form.notas}
                                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                                    placeholder="Observaciones..."
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit" data-testid="cierre-submit-btn">
                                    Cerrar Caja
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="animate-fadeIn">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Hoy</p>
                                <p className="text-2xl font-heading font-bold text-primary">
                                    {formatCurrency(totalSistema)}
                                </p>
                            </div>
                            <div className="p-3 rounded-lg bg-primary/10">
                                <TrendingUp className="w-6 h-6 text-primary" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-1">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Pagos Registrados</p>
                                <p className="text-2xl font-heading font-bold">{pagosHoy.length}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-accent/10">
                                <Receipt className="w-6 h-6 text-accent" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-2">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Usuario</p>
                                <p className="text-lg font-heading font-bold">{user?.nombre}</p>
                                <p className="text-xs text-muted-foreground capitalize">{user?.rol}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted">
                                <Calculator className="w-6 h-6 text-muted-foreground" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pagos del día */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">Pagos del Día</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Hora</TableHead>
                                    <TableHead>Monto</TableHead>
                                    <TableHead>Forma</TableHead>
                                    <TableHead>Registrado por</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pagosHoy.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8">
                                            <p className="text-muted-foreground">No hay pagos registrados hoy</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pagosHoy.map((pago) => (
                                        <TableRow key={pago.id}>
                                            <TableCell className="font-mono">
                                                {pago.fecha.split('T')[1]?.slice(0, 5) || '-'}
                                            </TableCell>
                                            <TableCell className="font-mono font-bold">
                                                {formatCurrency(pago.monto)}
                                            </TableCell>
                                            <TableCell className="capitalize">{pago.forma_pago}</TableCell>
                                            <TableCell>{pago.registrado_por}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Historial de Cierres */}
            {hasRole(['admin', 'supervisor']) && cierres.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Historial de Cierres</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Usuario</TableHead>
                                        <TableHead>Sistema</TableHead>
                                        <TableHead>Entregado</TableHead>
                                        <TableHead>Diferencia</TableHead>
                                        <TableHead>Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {cierres.slice(0, 10).map((cierre) => (
                                        <TableRow key={cierre.id}>
                                            <TableCell>{cierre.fecha}</TableCell>
                                            <TableCell>{cierre.usuario_nombre}</TableCell>
                                            <TableCell className="font-mono">
                                                {formatCurrency(cierre.total_sistema)}
                                            </TableCell>
                                            <TableCell className="font-mono">
                                                {formatCurrency(cierre.efectivo_entregado)}
                                            </TableCell>
                                            <TableCell className="font-mono">
                                                {formatCurrency(cierre.diferencia)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline" 
                                                    className={
                                                        cierre.estado === 'cuadrado' 
                                                            ? 'bg-accent/10 text-accent' 
                                                            : cierre.estado === 'faltante'
                                                                ? 'bg-destructive/10 text-destructive'
                                                                : 'bg-warning/10 text-warning'
                                                    }
                                                >
                                                    {cierre.estado === 'cuadrado' && <CheckCircle className="w-3 h-3 mr-1" />}
                                                    {cierre.estado === 'faltante' && <AlertCircle className="w-3 h-3 mr-1" />}
                                                    {cierre.estado.charAt(0).toUpperCase() + cierre.estado.slice(1)}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
